import React, { useState } from "react";

import { DeleteDialog, Modal } from "../components";
import Panel from "../components/panels/panel";

import { ButtonSecondary } from "components/Atoms/Button";
import {
  ExclusivityCheckResultMessage,
  ExclusivityClauseDetails
} from "./ExclusivityClauseMessages";
import { ExclusivityCheckFields } from "./utils";
import { ModifyExclusivityClauseModal } from "./ModifyExclusivityClauseModal";
import { ExclusivityConflictsModal } from "./ExclusivityConflictsModal";
import { useExclusivityCheckFields, useExclusivityConflictCount } from "./hooks";

interface ExclusivityPanelProps {
  show: server.dto.Show
  canModifyShow: boolean
  exclusivityFields: ExclusivityCheckFields
  config: server.dto.ShowExclusivityPanelConfig
}

export const ExclusivityPanel = (props: ExclusivityPanelProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExclusivityConflictsModalOpen, setIsExclusivityConflictsModalOpen] = useState(false);
  const [isModifyExclusivityModalOpen, setIsModifyExclusivityConflictsModalOpen] = useState(false);

  // exclusivityClause is locally owned becsaue it can be modified/deleted from this panel
  const [exclusivityClause, setExclusivityClause] = useState(props.exclusivityFields.exclusivityClause);
  const exclusivityCheckFields = useExclusivityCheckFields({
    fields: props.exclusivityFields,
    exclusivityClause,
  });

  const { data, isLoading, isError } = useExclusivityConflictCount({ exclusivityCheckFields });

  const deleteExclusivityClause = async () => {
    setIsDeleting(false);
    await window.CoreApi.Show.DeleteExclusivityClause({ showId: props.show.showId });
    setExclusivityClause(null);
  }

  if (isError) {
    return <Panel
      panelClassName="s1_related-feature-panel"
      title={props.config.headerLabel}
      hasError
    />
  }

  if (isLoading) {
    return <Panel
      panelClassName="s1_related-feature-panel"
      title={props.config.headerLabel}
      isLoading
    />
  }

  const canAddClause = props.canModifyShow && data.exclusivityConflictErrors.length === 0 && props.config.canAdd;
  const buttonLabel = exclusivityClause ? RESX.SoUser.Modify : RESX.GeneralLabels.resxNew;

  return (
    <div>
      <ExclusivityConflictsModal
        isOpen={isExclusivityConflictsModalOpen}
        handleClose={() => setIsExclusivityConflictsModalOpen(false)}
        activeShow={props.show}
        exclusivityCheckFields={exclusivityCheckFields}
      />
      <Modal
        title={RESX.Exclusivity.resxExclusivity}
        isOpen={isModifyExclusivityModalOpen}
        handleClose={() => setIsModifyExclusivityConflictsModalOpen(false)}
        width="small"
        className="unset_z">
        <ModifyExclusivityClauseModal
          showId={props.show.showId}
          clause={exclusivityClause}
          onSaved={(newClause) => {
            setExclusivityClause(newClause);
            setIsModifyExclusivityConflictsModalOpen(false);
          }}
        />
      </Modal>
      <DeleteDialog
        isOpen={isDeleting}
        onDelete={deleteExclusivityClause}
        message={RESX.GeneralWarnings.AreYouSure}
        handleClose={() => { setIsDeleting(false); }}
      />
      <Panel
        panelClassName="s1_related-feature-panel"
        title={props.config.headerLabel}
        button={canAddClause && <ButtonSecondary onClick={() => setIsModifyExclusivityConflictsModalOpen(true)} label={buttonLabel} />}>
        <div className="flex-column gap-big">
          <ExclusivityClauseDetails
            result={data}
            clause={exclusivityClause}
            onDelete={() => setIsDeleting(true)}
          />
          <ExclusivityCheckResultMessage
            openExclusivityConflictModal={() => setIsExclusivityConflictsModalOpen(true)}
            result={data}
          />
        </div>
      </Panel>
    </div>
  );
} 
